import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity'
import { Action, createReducer, on } from '@ngrx/store'

import { initialDisplayColumns } from '@galabid/gb-dashboard/event-tickets/utils'
import { GbTableColumn, GridSwitchEnum } from '@galabid/gb-dashboard/shared/feature'

import { formatForQuillEditor } from 'libs/gb-dashboard/shared/ui/src/lib/gb-quill-editor/gb-quill-editor-formatter'
import * as EventTicketPackagesActions from './event-ticket-packages.actions'
import { EventTicketPackageEntity, EventTicketPackageFilters } from './event-ticket-packages.models'

export const EVENT_TICKET_PACKAGES_FEATURE_KEY = 'eventTicketPackages'

export interface EventTicketPackagesState extends EntityState<EventTicketPackageEntity> {
  selectedId?: string | number
  loaded: boolean
  count: number
  dataView: GridSwitchEnum
  displayColumns: GbTableColumn[]
  filters: EventTicketPackageFilters
  editCreateLoaded: boolean
}

export interface EventTicketPackagesPartialState {
  readonly [EVENT_TICKET_PACKAGES_FEATURE_KEY]: EventTicketPackagesState
}

export const eventTicketPackagesAdapter: EntityAdapter<EventTicketPackageEntity> =
  createEntityAdapter<EventTicketPackageEntity>({
    selectId: (eventTicketPackage: EventTicketPackageEntity) => eventTicketPackage.uuid,
  })

export const initialEventTicketPackagesState: EventTicketPackagesState = eventTicketPackagesAdapter.getInitialState({
  loaded: true,
  count: 0,
  dataView: GridSwitchEnum.Grid,
  displayColumns: initialDisplayColumns,
  filters: {},
  editCreateLoaded: true,
})

const eventTicketPackagesReducer = createReducer(
  initialEventTicketPackagesState,
  on(EventTicketPackagesActions.loadEventTicketPackages, (state) => ({
    ...state,
    filters: {},
    loaded: false,
    error: null,
  })),
  on(EventTicketPackagesActions.setEventTicketPackages, (state, action) =>
    eventTicketPackagesAdapter.setAll(action.eventTicketPackages, {
      ...state,
      count: action.count,
      filters: action.filters,
      loaded: true,
    }),
  ),
  on(EventTicketPackagesActions.loadEventTicketPackagesSuccess, (state, action) =>
    eventTicketPackagesAdapter.upsertMany(action.eventTicketPackages, {
      ...state,
      count: action.count,
      filters: action.filters,
      loaded: true,
    }),
  ),
  on(EventTicketPackagesActions.loadEventTicketPackagesFailure, (state, { error }) => ({ ...state, error })),
  on(EventTicketPackagesActions.updateDisplayColumns, (state, { displayColumns }) => ({ ...state, displayColumns })),
  on(EventTicketPackagesActions.updateDataView, (state, { dataView }) => ({ ...state, dataView })),
  on(EventTicketPackagesActions.syncEventTicketPackageSuccess, (state, { eventTicketPackage }) =>
    eventTicketPackagesAdapter.upsertOne(
      { ...eventTicketPackage, description: formatForQuillEditor(eventTicketPackage.description) },
      state,
    ),
  ),
  on(EventTicketPackagesActions.updateSelectedEventTicketPackage, (state) => ({
    ...state,
    editCreateLoaded: false,
  })),
  on(EventTicketPackagesActions.updateSelectedEventTicketPackageSuccess, (state, action) =>
    eventTicketPackagesAdapter.updateOne(action.update, { ...state, editCreateLoaded: true }),
  ),
  on(EventTicketPackagesActions.updateEventTicketPackageForRealtime, (state, action) =>
    eventTicketPackagesAdapter.updateOne(action.update, state),
  ),
  on(EventTicketPackagesActions.updateSelectedEventTicketPackageFailure, (state) => ({
    ...state,
    editCreateLoaded: true,
  })),
  on(EventTicketPackagesActions.updateSelectedTicketPackageImages, (state) => ({
    ...state,
    editCreateLoaded: false,
  })),
  on(EventTicketPackagesActions.createEventTicketPackage, (state) => ({
    ...state,
    editCreateLoaded: false,
  })),
  on(EventTicketPackagesActions.createEventTicketPackageSuccess, (state) => ({
    ...state,
    editCreateLoaded: true,
  })),
  on(EventTicketPackagesActions.createEventTicketPackageFailure, (state) => ({
    ...state,
    editCreateLoaded: true,
  })),
  on(EventTicketPackagesActions.deleteEventTicketPackageSuccess, (state, action) =>
    eventTicketPackagesAdapter.removeOne(action.uuid, { ...state, count: state.count - 1 }),
  ),
  on(EventTicketPackagesActions.bulkDeleteEventTicketPackagesSuccess, (state, action) =>
    eventTicketPackagesAdapter.removeMany(action.ticketPackages, {
      ...state,
      count: state.count - action.ticketPackages.length,
    }),
  ),
  on(
    EventTicketPackagesActions.selectEventTicketPackage,
    // update selected ticket package on dialog open
    EventTicketPackagesActions.openEventTicketPurchaseDialog,
    (state, { uuid }) => ({ ...state, selectedId: uuid }),
  ),
  on(EventTicketPackagesActions.reorderEventTicketPackages, (state) => ({ ...state, loaded: false })),
  on(EventTicketPackagesActions.clearSelectedEventTicketPackage, (state) => ({
    ...state,
    selectedId: initialEventTicketPackagesState.selectedId,
  })),
  on(EventTicketPackagesActions.clearEventTicketPackages, (state) => ({
    ...initialEventTicketPackagesState,
    dataView: state.dataView,
  })),
)

export function reducer(state: EventTicketPackagesState | undefined, action: Action) {
  return eventTicketPackagesReducer(state, action)
}
